import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { ChipComponent } from '@claas/claas-ui-components';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FileUploadState,
  SapUploadParameters,
  UploadConfig,
  UploadResponse,
  Visibility,
  fileUploadKey,
} from './attachment.model';
import { SapFileUploadService } from './sap-file-upload.service';
import { FileUploader } from './uploader/uploader';

export type { Visibility } from './attachment.model';

function parseVisibility(value: string | undefined): Visibility | undefined {
  return value === 'ClaasInternal' || value === 'DealerTypeA' || value === 'DealerTypeB'
    ? value
    : undefined;
}

@Component({
  selector: 'app-attachment-uploader',
  templateUrl: './attachment.html',
  styleUrls: ['./attachment.scss'],
  imports: [FileUploader, ChipComponent, TranslatePipe],
})
export class AttachmentUploader implements OnInit {
  public readonly visibility = input(undefined, { transform: parseVisibility });
  public readonly position = input<string>();
  public readonly category = input<string>();
  public readonly aggregateType = input<string>();
  public readonly aggregateId = input<string>();
  public readonly jwt = input<string>();
  /** Namentlich bekannter Benutzer; überschreibt den technischen User aus dem Token. */
  public readonly uploadUser = input<string>();

  protected readonly hasAggregateReference = computed(() => {
    const aggregateType = this.aggregateType()?.trim();
    const aggregateId = this.aggregateId()?.trim();
    return !!aggregateType && !!aggregateId;
  });

  protected readonly isUploading = signal(false);
  protected readonly fileStates = signal<ReadonlyMap<string, FileUploadState>>(new Map());
  protected readonly parameterError = signal('');
  protected readonly tags = signal<string[]>([]);
  protected readonly tagDraft = signal('');
  /**
   * Bewusster „Glitch": Der serverseitige Pro-Datei-Wert {@code maxFileSizeBytes}
   * wird hier als **Gesamt-Request-Limit** verwendet (an den Uploader als
   * `maxUploadSize` durchgereicht). Strenger als die Server-Grenze – gewollt, da
   * es kein serverseitiges Gesamt-Request-Limit gibt (siehe UploadConfig).
   */
  protected readonly maxTotalSize = computed(() => this.config()?.maxFileSizeBytes);
  protected readonly maxFiles = computed(() => this.config()?.maxFiles);
  protected readonly allowedFileTypes = computed(() => this.config()?.allowedFileTypes ?? []);
  protected readonly forbiddenFileTypes = computed(() => this.config()?.forbiddenFileTypes ?? []);
  protected readonly isCompleted = computed(() => {
    const states = this.fileStates();
    return (
      states.size > 0 &&
      [...states.values()].every((state) => state.status === 'success' || state.status === 'error')
    );
  });
  protected readonly configError = computed(() => this.configStatus() === 'error');
  /** Solange die Config nicht erfolgreich geladen ist, bleibt der Upload gesperrt. */
  protected readonly uploadDisabled = computed(
    () => this.isUploading() || this.configStatus() !== 'ready',
  );

  private readonly config = signal<UploadConfig | undefined>(undefined);
  private readonly configStatus = signal<'loading' | 'ready' | 'error'>('loading');
  private readonly fileUploadService = inject(SapFileUploadService);
  private readonly translate = inject(TranslateService);

  public ngOnInit(): void {
    this.configStatus.set('loading');
    this.fileUploadService.getConfig(this.jwt()).subscribe({
      next: (config) => {
        this.config.set(config);
        this.configStatus.set('ready');
      },
      error: () => {
        this.config.set(undefined);
        this.configStatus.set('error');
      },
    });
  }

  protected onTagDraftChange(value: string): void {
    // Komma committet die bisher getippten Tags sofort.
    if (value.includes(',')) {
      this.addTags(value);
      this.tagDraft.set('');
      return;
    }
    this.tagDraft.set(value);
  }

  protected commitTagDraft(): void {
    this.addTags(this.tagDraft());
    this.tagDraft.set('');
  }

  protected removeTag(tag: string): void {
    this.tags.update((current) => current.filter((existing) => existing !== tag));
  }

  protected handleUpload(files: File[]): void {
    // Noch nicht bestätigten Tag-Entwurf vor dem Upload übernehmen.
    this.commitTagDraft();
    const parameters = this.buildParameters(this.tags());
    if (!parameters) {
      this.parameterError.set(this.translate.instant('errors.missingParameters'));
      return;
    }
    this.parameterError.set('');

    const initial = new Map<string, FileUploadState>();
    for (const file of files) {
      initial.set(fileUploadKey(file), { status: 'uploading', progress: 0 });
    }
    this.fileStates.set(initial);
    this.isUploading.set(true);

    // Der Upload-Endpunkt akzeptiert genau eine Datei pro Request (Binary-Body) –
    // je Datei wird daher ein eigener Request ausgelöst und unabhängig verfolgt.
    let pending = files.length;
    const onSettled = (): void => {
      pending -= 1;
      if (pending <= 0) {
        this.isUploading.set(false);
      }
    };

    for (const file of files) {
      this.fileUploadService.upload(file, parameters, this.jwt()).subscribe({
        next: (event) => {
          if (event.type === 'progress') {
            this.updateFileProgress(file, event.progress);
          } else {
            this.applyResponse(file, event.response);
          }
        },
        error: (error: unknown) => {
          this.failFile(file, this.toMessage(error));
          onSettled();
        },
        complete: onSettled,
      });
    }
  }

  private addTags(raw: string): void {
    const candidates = raw
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    if (candidates.length === 0) {
      return;
    }
    this.tags.update((current) => {
      const seen = new Set(current.map((tag) => tag.toLowerCase()));
      const result = [...current];
      for (const candidate of candidates) {
        const key = candidate.toLowerCase();
        if (!seen.has(key)) {
          result.push(candidate);
          seen.add(key);
        }
      }
      return result;
    });
  }

  private buildParameters(tags: string[]): SapUploadParameters | undefined {
    const visibility = this.visibility();
    const position = this.position();
    const aggregateType = this.aggregateType();
    const aggregateId = this.aggregateId();

    if (!visibility || !position || !aggregateType || !aggregateId) {
      return undefined;
    }

    return {
      visibility,
      position,
      aggregateType,
      aggregateId,
      category: this.category(),
      tags,
      uploadUser: this.uploadUser(),
    };
  }

  private updateFileProgress(file: File, progress: number): void {
    const key = fileUploadKey(file);
    this.fileStates.update((current) => {
      const state = current.get(key);
      if (!state || state.status !== 'uploading') {
        return current;
      }
      const next = new Map(current);
      next.set(key, { ...state, progress });
      return next;
    });
  }

  private applyResponse(file: File, response: UploadResponse): void {
    const key = fileUploadKey(file);
    this.fileStates.update((current) => {
      const next = new Map(current);
      if (response.success) {
        next.set(key, { status: 'success', progress: 100 });
      } else {
        next.set(key, {
          status: 'error',
          progress: 100,
          message: response.message ?? this.translate.instant('errors.uploadFailed'),
        });
      }
      return next;
    });
  }

  private failFile(file: File, message: string): void {
    this.fileStates.update((current) => {
      const next = new Map(current);
      next.set(fileUploadKey(file), { status: 'error', progress: 100, message });
      return next;
    });
  }

  private toMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = typeof error.error === 'string' ? error.error : error.message;
      return this.translate.instant('errors.uploadFailedHttp', { status: error.status, detail });
    }
    return this.translate.instant('errors.uploadFailedUnknown');
  }
}
